import type {BuildId, MatrixParameter} from '../../../../types'
import {emptyArray} from '../../../../utils/empty'
import {objectKeys} from '../../../../utils/object'
import {StateOptions, BuildState} from '../../../common/BuildStateFilter/BuildStateFilter.constants'
import {useQueryStateMatrixBuild} from '../MatrixBuild.hooks'
import {getIsVisibleBuild} from '../MatrixBuild.utils'

export const useAvailableStates = () =>
  objectKeys(StateOptions).filter(key => key !== BuildState.PINNED && key !== BuildState.STARRED)

export const useAvailableParameters = (
  buildId: BuildId,
  stateFilter: BuildState,
): MatrixParameter[] => {
  const {dependencies, parameters, buildsHash} = useQueryStateMatrixBuild(buildId)

  if (dependencies.length === 0 || parameters.length === 0) {
    return emptyArray as MatrixParameter[]
  }

  const availableParametersHash = parameters.reduce<Record<string, Set<String>>>(
    (acc, parameter) => {
      if (parameter.name) {
        return {...acc, [parameter.name]: new Set<string>()}
      }
      return acc
    },
    {},
  )

  dependencies.forEach(({build: id, parameters: dependencyParameters}) => {
    const build = id != null ? buildsHash?.[id] : null
    if (getIsVisibleBuild(build, stateFilter)) {
      dependencyParameters?.entry?.forEach(({name, value}) => {
        if (name != null && value != null) {
          availableParametersHash[name].add(value)
        }
      })
    }
  })

  return (
    parameters.map(parameter => {
      const {name, value} = parameter

      if (name == null || value == null) {
        return {
          name,
          value,
        }
      }

      return {
        name,
        value: value.filter(valueItem => availableParametersHash[name].has(valueItem.value)),
      }
    }) ?? (emptyArray as MatrixParameter<string>[])
  )
}
